import {
  Conversation,
  RequestACallDetails,
} from '@stripe-internal/conversations-client';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {RequestACallCodesEndpointResponseSuccess} from '@stripe-internal/conversations-shared-dependencies';
import {useProfile} from '../../Providers/ProfileProvider';
import {useApi} from '../../Providers/ApiProvider/ApiProvider';
import {ConfigContext} from '../../Providers/ConfigProvider';
import {useMountEffect} from '../../useMountEffect';

type Props = {
  conversation?: Conversation;
};

type VerificationCodes = {
  agentCode: string;
  userCode: string;
};

type RacStatus = RequestACallCodesEndpointResponseSuccess['racStatus'];

const STATUS_POLLING_FREQUENCY_MS = 5_000; // 5 seconds

export const useRequestACallDetails = ({conversation}: Props) => {
  const {entrypoint} = useContext(ConfigContext);
  const {profile} = useProfile();
  const {visitor, featureFlags} = profile;
  const {fetchRequestACallCodes, cancelRequestACall} = useApi();

  const [verificationCodes, setVerificationCodes] = useState<
    VerificationCodes | undefined
  >();
  const currTimeout = useRef<NodeJS.Timeout>();
  const racDoneStrikes = useRef<number>(0);
  const isInitialLoading = useRef<boolean>(true);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [racStatus, setRacStatus] = useState<RacStatus>('pending');
  const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);

  const [racDetails, setRacDetails] = useState<RequestACallDetails | undefined>(
    conversation?.context.requestACallDetails,
  );

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const sub = conversation
      .contextObservable()
      .subscribe((context) => setRacDetails(context.requestACallDetails));
    return () => sub.unsubscribe();
  }, [conversation]);

  const userName: string = useMemo(
    () => racDetails?.userName || visitor.displayName || '',
    [racDetails, visitor],
  );

  const userPhoneNumber: string = useMemo(
    () => racDetails?.userPhoneNumber || visitor.phoneNumber || '',
    [racDetails, visitor],
  );

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    fetchRequestACallCodes(entrypoint, conversation?.id)
      .then((resp) => {
        if (resp.type === 'success') {
          if (!resp.pending) {
            racDoneStrikes.current += 1;

            if (racDoneStrikes.current >= 2) {
              racDoneStrikes.current = 0;
              setCallEnded(true);
              return;
            }
          } else {
            setVerificationCodes({
              agentCode: resp.agentCode || '',
              userCode: resp.userCode || '',
            });
          }

          isInitialLoading.current = false;
          setRacStatus(resp.racStatus ?? 'calling');

          const nextReloadInMs = resp.nextReload
            ? resp.nextReload * 1000
            : STATUS_POLLING_FREQUENCY_MS;

          const nextTimeout = Math.min(
            nextReloadInMs,
            STATUS_POLLING_FREQUENCY_MS,
          );
          currTimeout.current = setTimeout(fetchCodes, nextTimeout);
        } else {
          throw new Error('Unexpected RAC status response');
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [entrypoint, fetchRequestACallCodes, conversation?.id]);

  useMountEffect(() => {
    fetchCodes();
    return () => clearTimeout(currTimeout.current);
  });

  const cancelRac = useCallback(async () => {
    setIsCancelLoading(true);
    cancelRequestACall(entrypoint, conversation?.id)
      .then((resp) => {
        if (resp.type === 'success') {
          setCallEnded(true);
          conversation?.setContextValues({
            ...conversation.context,
            requestACallDetails: {
              userName,
              userPhoneNumber,
              callCanceled: true,
            },
          });
        } else if (resp.type === 'conflict' || resp.type === 'not-found') {
          setIsCancelLoading(false);
          setRacStatus('pending');
        } else {
          throw new Error('Unexpected RAC cancel response');
        }
      })
      .catch(() => {
        setIsCancelLoading(false);
        setRacStatus('pending');
      });
  }, [cancelRequestACall, entrypoint, conversation, userName, userPhoneNumber]);

  return {
    userName,
    userPhoneNumber,
    error,
    loading,
    verificationCodes,
    isInitialLoading: isInitialLoading.current,
    racStatus: racStatus ?? 'calling',
    isCancelLoading: isCancelLoading || isInitialLoading.current,
    cancelRac,
    callEnded,
  };
};
